import { useState } from 'react'
import { Copy, Check, Lock } from 'lucide-react'
import { Task } from '@renderer/types/api'

type SidebarTheme = 'light' | 'dark'

interface ThemeTokens {
  headerBorder: string
  eyebrow: string
  title: string
  trackBg: string
  fill: string
  counter: string
  tutorLabel: string
  tutorBorder: string
  tutorActiveBg: string
  tutorActiveText: string
  tutorInactiveText: string
  lockOverlayBg: string
  lockIcon: string
  lockText: string
  noticeBg: string
  noticeText: string
  emptyText: string
  rowBorder: string
  rowBorderActive: string
  rowBg: string
  rowBgActive: string
  rowBgHover: string
  refCode: string
  refCodeActive: string
  taskTitle: string
  taskTitleActive: string
  taskTitleDone: string
  doneStrike: string
  markerPendingBorder: string
  markerPendingText: string
  markerDoneBg: string
  markerDoneCheck: string
  markerUnverifiedBorder: string
  markerActiveBorder: string
  markerActiveGlow: string
  markerActiveDot: string
}

const LIGHT_TOKENS: ThemeTokens = {
  headerBorder: '#f0e6cd',
  eyebrow: '#b8923a',
  title: '#1d160a',
  trackBg: '#efe5cb',
  fill: 'linear-gradient(90deg, #7a5a1e, #b8923a)',
  counter: '#4a3f2a',
  tutorLabel: '#7a6e54',
  tutorBorder: '#e4d6b0',
  tutorActiveBg: '#7a5a1e',
  tutorActiveText: '#ffffff',
  tutorInactiveText: '#7a6e54',
  lockOverlayBg: 'rgba(250,244,227,0.82)',
  lockIcon: '#b8923a',
  lockText: '#7a6e54',
  noticeBg: '#f3e8c8',
  noticeText: '#7a5a1e',
  emptyText: '#a89c7e',
  rowBorder: 'transparent',
  rowBorderActive: 'transparent',
  rowBg: 'transparent',
  rowBgActive: '#f3e8c8',
  rowBgHover: '#faf4e3',
  refCode: '#7a6e54',
  refCodeActive: '#b8923a',
  taskTitle: '#4a3f2a',
  taskTitleActive: '#1d160a',
  taskTitleDone: '#7a6e54',
  doneStrike: '#a89c7e',
  markerPendingBorder: '#e4d6b0',
  markerPendingText: '#a89c7e',
  markerDoneBg: '#7a5a1e',
  markerDoneCheck: '#ffffff',
  markerUnverifiedBorder: '#7a5a1e',
  markerActiveBorder: '#b8923a',
  markerActiveGlow: 'rgba(184, 146, 58, 0.18)',
  markerActiveDot: '#b8923a'
}

const DARK_TOKENS: ThemeTokens = {
  headerBorder: '#3a2f1a',
  eyebrow: '#d4a94a',
  title: '#f5ecd6',
  trackBg: '#3a2f1a',
  fill: 'linear-gradient(90deg, #8a6a1e, #d4a94a)',
  counter: '#d4a94a',
  tutorLabel: '#8a7f5e',
  tutorBorder: '#3a2f1a',
  tutorActiveBg: '#7a5a1e',
  tutorActiveText: '#ffffff',
  tutorInactiveText: '#8a7f5e',
  lockOverlayBg: 'rgba(28,23,18,0.88)',
  lockIcon: '#d4a94a',
  lockText: '#b3a788',
  noticeBg: 'rgba(212,169,74,0.1)',
  noticeText: '#d4a94a',
  emptyText: '#8a7f5e',
  rowBorder: '#3a2f1a',
  rowBorderActive: '#d4a94a',
  rowBg: 'transparent',
  rowBgActive: 'rgba(212,169,74,0.08)',
  rowBgHover: 'rgba(255,255,255,0.03)',
  refCode: '#8a7f5e',
  refCodeActive: '#d4a94a',
  taskTitle: '#b3a788',
  taskTitleActive: '#f5ecd6',
  taskTitleDone: '#8a7f5e',
  doneStrike: '#6b6354',
  markerPendingBorder: 'rgba(212,169,74,0.35)',
  markerPendingText: '#b3a788',
  markerDoneBg: '#7a5a1e',
  markerDoneCheck: '#ffffff',
  markerUnverifiedBorder: '#d4a94a',
  markerActiveBorder: '#d4a94a',
  markerActiveGlow: 'rgba(212,169,74,0.22)',
  markerActiveDot: '#d4a94a'
}

interface TasksSidebarProps {
  tasks: Task[]
  completedTaskIds: number[]
  unverifiedTaskIds?: number[]
  currentTaskId?: number
  onSelectTask: (task: Task) => void
  moduleTitle?: string
  exerciseTitle?: string
  exerciseRefCode?: string
  // Optional terminal command hint shown in the footer
  // (e.g., { label: 'Run on cluster3', command: 'ssh admin@192.168.0.103' })
  terminalHint?: { label: string; command: string } | null
  currentStep?: number
  totalSteps?: number
  sessionActive?: boolean
  noticeText?: string | null
  teachingMode?: 'guide' | 'assist'
  onTeachingModeChange?: (mode: 'guide' | 'assist') => void
  theme?: SidebarTheme
}

export const TasksSidebar: React.FC<TasksSidebarProps> = ({
  tasks,
  completedTaskIds,
  unverifiedTaskIds = [],
  noticeText,
  currentTaskId,
  onSelectTask,
  moduleTitle,
  exerciseTitle,
  exerciseRefCode,
  terminalHint,
  sessionActive = false,
  teachingMode,
  onTeachingModeChange,
  theme = 'light'
}) => {
  const t = theme === 'dark' ? DARK_TOKENS : LIGHT_TOKENS
  const completed = completedTaskIds.length
  const total = tasks.length
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0

  // Derive the eyebrow: "M2.E1 · CLUSTER SETUP"
  const eyebrowParts: string[] = []
  if (exerciseRefCode) eyebrowParts.push(exerciseRefCode)
  if (moduleTitle) eyebrowParts.push(moduleTitle.toUpperCase())

  return (
    <div className="h-full flex flex-col" style={{ background: 'inherit' }}>
      {/* Header */}
      <div
        className="shrink-0 px-5 pt-5 pb-3 border-b"
        style={{ borderColor: t.headerBorder }}
      >
        {eyebrowParts.length > 0 && (
          <p
            className="text-[11px] font-semibold tracking-[0.12em] uppercase mb-1.5"
            style={{ color: t.eyebrow }}
          >
            {eyebrowParts.join(' · ')}
          </p>
        )}
        {exerciseTitle && (
          <h2
            className="text-[16px] font-bold leading-snug mb-3"
            style={{ color: t.title, letterSpacing: '-0.01em' }}
          >
            {exerciseTitle}
          </h2>
        )}
        {total > 0 && (
          <div className="flex items-center gap-2.5">
            <div
              className="flex-1 h-1 rounded-full overflow-hidden"
              style={{ background: t.trackBg }}
            >
              <div
                className="h-full transition-all duration-500 ease-out"
                style={{
                  width: `${pct}%`,
                  background: t.fill
                }}
              />
            </div>
            <span
              className="text-[12px] font-medium tabular-nums"
              style={{ color: t.counter }}
            >
              {completed}/{total}
            </span>
          </div>
        )}

        {teachingMode && onTeachingModeChange && sessionActive && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[11px] shrink-0" style={{ color: t.tutorLabel }}>
              Tutor
            </span>
            <div
              className="flex rounded-md border overflow-hidden"
              style={{ borderColor: t.tutorBorder }}
            >
              {(['guide', 'assist'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onTeachingModeChange(m)}
                  aria-pressed={teachingMode === m}
                  title={
                    m === 'guide'
                      ? 'Guide me step by step'
                      : 'Assist me when I need help'
                  }
                  className="px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors"
                  style={{
                    background: teachingMode === m ? t.tutorActiveBg : 'transparent',
                    color: teachingMode === m ? t.tutorActiveText : t.tutorInactiveText
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Task list */}
      <div className="flex-1 relative overflow-y-auto px-2.5 py-2.5 flex flex-col gap-2">
        {!sessionActive && tasks.length > 0 && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md"
            style={{ background: t.lockOverlayBg, backdropFilter: 'blur(1px)' }}
          >
            <Lock className="w-4 h-4" style={{ color: t.lockIcon }} />
            <p className="text-[12px] font-medium text-center px-4" style={{ color: t.lockText }}>
              Start a session to interact with tasks
            </p>
          </div>
        )}
        {noticeText && (
          <div
            className="px-3 py-2 rounded-md text-[12px]"
            style={{ background: t.noticeBg, color: t.noticeText }}
            role="status"
          >
            {noticeText}
          </div>
        )}
        {tasks.length === 0 ? (
          <div
            className="text-center text-[13px] py-8"
            style={{ color: t.emptyText }}
          >
            No tasks available
          </div>
        ) : (
          tasks.map((task, idx) => {
            const isCompleted = completedTaskIds.includes(task.id)
            const isUnverified = isCompleted && unverifiedTaskIds.includes(task.id)
            const isCurrent = currentTaskId === task.id
            const isClickable = !isCurrent && (!isCompleted || isUnverified)
            const state = isCompleted
              ? (isUnverified ? 'done-unverified' : 'done')
              : isCurrent
                ? 'active'
                : 'pending'
            return (
              <TaskRow
                key={task.id}
                task={task}
                number={idx + 1}
                state={state}
                tokens={t}
                onClick={() => isClickable && onSelectTask(task)}
              />
            )
          })
        )}
      </div>

      {/* Terminal hint footer */}
      {terminalHint && <TerminalHint label={terminalHint.label} command={terminalHint.command} />}
    </div>
  )
}

type RowState = 'pending' | 'active' | 'done' | 'done-unverified'

const TaskRow: React.FC<{
  task: Task
  number: number
  state: RowState
  tokens: ThemeTokens
  onClick: () => void
}> = ({ task, number, state, tokens: t, onClick }) => {
  const isActive = state === 'active'
  const isUnverified = state === 'done-unverified'
  const isDone = state === 'done' || isUnverified
  const hoverable = state === 'pending' || isUnverified

  return (
    <div
      onClick={onClick}
      className="relative pl-11 pr-3 py-3 rounded-lg border transition-colors"
      style={{
        cursor: hoverable ? 'pointer' : 'default',
        borderColor: isActive ? t.rowBorderActive : t.rowBorder,
        background: isActive ? t.rowBgActive : t.rowBg
      }}
      title={
        isUnverified
          ? "Marked done from what was on screen — click to go back to it"
          : undefined
      }
      onMouseEnter={(e) => {
        if (hoverable) e.currentTarget.style.background = t.rowBgHover
      }}
      onMouseLeave={(e) => {
        if (hoverable) e.currentTarget.style.background = isActive ? t.rowBgActive : t.rowBg
      }}
    >
      {/* Marker */}
      <Marker state={state} number={number} tokens={t} />

      {/* Step code */}
      {task.ref_code && (
        <p
          className="text-[10px] font-semibold tracking-[0.08em] uppercase mb-0.5"
          style={{ color: isActive ? t.refCodeActive : t.refCode }}
        >
          {task.ref_code}
        </p>
      )}

      {/* Title */}
      <p
        className="text-[13px] leading-snug"
        style={{
          color: isActive ? t.taskTitleActive : isDone ? t.taskTitleDone : t.taskTitle,
          fontWeight: isActive ? 600 : 500,
          textDecoration: isDone ? 'line-through' : undefined,
          textDecorationColor: isDone ? t.doneStrike : undefined
        }}
      >
        {task.title}
      </p>
    </div>
  )
}

const Marker: React.FC<{ state: RowState; number: number; tokens: ThemeTokens }> = ({
  state,
  number,
  tokens: t
}) => {
  if (state === 'done-unverified') {
    return (
      <div
        className="absolute left-3 top-3 w-[22px] h-[22px] rounded-full grid place-items-center"
        style={{ background: 'transparent', border: `1.5px solid ${t.markerUnverifiedBorder}` }}
      >
        <Check className="w-[11px] h-[11px]" style={{ color: t.markerUnverifiedBorder }} strokeWidth={3} />
      </div>
    )
  }
  if (state === 'done') {
    return (
      <div
        className="absolute left-3 top-3 w-[22px] h-[22px] rounded-full grid place-items-center"
        style={{ background: t.markerDoneBg }}
      >
        <Check className="w-[11px] h-[11px]" style={{ color: t.markerDoneCheck }} strokeWidth={3} />
      </div>
    )
  }
  if (state === 'active') {
    return (
      <div
        className="absolute left-3 top-3 w-[22px] h-[22px] rounded-full grid place-items-center"
        style={{
          background: 'transparent',
          border: `1.5px solid ${t.markerActiveBorder}`,
          boxShadow: `0 0 0 3px ${t.markerActiveGlow}`
        }}
      >
        <div
          className="w-[7px] h-[7px] rounded-full"
          style={{ background: t.markerActiveDot, animation: 'tasks-pulse 1.6s infinite' }}
        />
        <style>{`
          @keyframes tasks-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(0.85); }
          }
        `}</style>
      </div>
    )
  }
  return (
    <div
      className="absolute left-3 top-3 w-[22px] h-[22px] rounded-full grid place-items-center"
      style={{ background: 'transparent', border: `1.5px solid ${t.markerPendingBorder}` }}
    >
      <span className="text-[11px] font-semibold" style={{ color: t.markerPendingText }}>
        {number}
      </span>
    </div>
  )
}

const TerminalHint: React.FC<{ label: string; command: string }> = ({ label, command }) => {
  const [copied, setCopied] = useState(false)

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(command)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  return (
    <div className="shrink-0 px-3 pb-3 pt-2" style={{ background: 'inherit' }}>
      <div
        className="rounded-md px-3 py-2.5"
        style={{ background: '#1d160a', border: '1px solid #2a1f10' }}
      >
        <div className="flex items-center justify-between mb-1">
          <span
            className="text-[10px] font-semibold tracking-[0.1em] uppercase"
            style={{ color: '#a89c7e' }}
          >
            {label}
          </span>
          <button
            onClick={onCopy}
            className="inline-flex items-center gap-1 text-[10px] transition-colors"
            style={{ color: copied ? '#b8923a' : '#a89c7e' }}
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'copy'}
          </button>
        </div>
        <code
          className="block text-[12px] break-all"
          style={{ color: '#e6c870', fontFamily: "'JetBrains Mono', monospace" }}
        >
          {command}
        </code>
      </div>
    </div>
  )
}
